/**
 * ProgressBarItemEditor - Individual progress bar item editor component
 *
 * Edits: image (conditional on kind), title (always), subtitle, description (conditional on itemType).
 * Includes two-thumb range slider for valueFrom/valueTo control.
 *
 * Key behavior: Moving a boundary only affects TWO adjacent items, not all items.
 *
 * Pattern adapted from SliderItemEditor.tsx
 */

import React, { useState, useLayoutEffect, useCallback, memo } from 'react';
import { Box, Typography, IconButton } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import DeleteIcon from '@mui/icons-material/Delete';
import LinkIcon from '@mui/icons-material/Link';
import { StyledTextField, MediaUploadField } from '@modules/ui-components';
import { DurationRangeSlider } from './DurationRangeSlider';
import type { ProgressBarItemEditorProps } from '../../../../types/progressBarSection.types';

/**
 * Local text input with blur-based sync
 * Prevents focus loss during parent re-renders
 */
interface LocalTextInputProps {
  value: string;
  onChange: (value: string) => void;
  disabled: boolean;
  label: string;
  placeholder?: string;
  testId: string;
  multiline?: boolean;
  rows?: number;
}

const LocalTextInput = memo<LocalTextInputProps>(function LocalTextInput({
  value,
  onChange,
  disabled,
  label,
  placeholder,
  testId,
  multiline = false,
  rows = 1,
}) {
  const [localValue, setLocalValue] = useState(value);

  // Sync local state when prop changes from external source
  useLayoutEffect(() => {
    setLocalValue(value);
  }, [value]);

  const handleBlur = useCallback(() => {
    if (localValue !== value) {
      onChange(localValue);
    }
  }, [localValue, value, onChange]);

  return (
    <StyledTextField
      value={localValue}
      onChange={(newValue) => setLocalValue(newValue)}
      onBlur={handleBlur}
      disabled={disabled}
      size="small"
      fullWidth
      placeholder={placeholder || label}
      aria-label={label}
      multiline={multiline}
      rows={rows}
      inputProps={{
        'data-testid': testId,
      }}
    />
  );
});

/**
 * ProgressBarItemEditor Component
 *
 * Layout:
 * [#N] [Link icon if connected] [Delete]
 * [====== Range Slider (two thumbs) ======]
 * [Range: 0 - 33 (33%) • 5.0s]
 * [Image Upload - conditional on kind]
 * [Title TextField - always visible]
 * [Subtitle TextField - conditional on itemType]
 * [Description TextField - conditional on itemType]
 */
export const ProgressBarItemEditor: React.FC<ProgressBarItemEditorProps> = ({
  index,
  title,
  subtitle,
  description,
  imageUrl,
  progressStart,
  progressEnd,
  range,
  totalDurationSeconds,
  isFirst,
  isLast,
  hasConnections,
  visibility,
  onTitleChange,
  onSubtitleChange,
  onDescriptionChange,
  onImageChange,
  onImageUpload,
  onRangeChange,
  onDelete,
  canDelete,
  disabled,
  renderTitleField,
  renderSubtitleField,
  renderDescriptionField,
}) => {
  const theme = useTheme();

  /**
   * Handle image change (URL or File)
   * Follows pattern from ListItemEditor
   */
  const handleImageChange = useCallback(
    async (value: string | File | null) => {
      if (value instanceof File) {
        try {
          const url = await onImageUpload(value);
          onImageChange(url);
        } catch (error) {
          console.error('Image upload failed:', error);
        }
      } else {
        onImageChange(value);
      }
    },
    [onImageUpload, onImageChange]
  );

  return (
    <Box
      data-testid={`progressbar-item-editor-${index}`}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1.5),
        p: theme.spacing(2),
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: theme.shape.borderRadius,
        backgroundColor: theme.palette.background.paper,
        width: '100%',
      }}
    >
      {/* Row 1: Index + Connection indicator + Delete */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 600,
              color: 'text.secondary',
              minWidth: 24,
            }}
          >
            #{index + 1}
          </Typography>
          {hasConnections && (
            <LinkIcon
              fontSize="small"
              sx={{ color: 'primary.main' }}
              titleAccess="Has timer action"
            />
          )}
        </Box>

        <IconButton
          data-testid={`progressbar-item-${index}-delete`}
          aria-label={`Delete item ${index + 1}`}
          onClick={onDelete}
          disabled={disabled || !canDelete}
          color="error"
          size="small"
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Row 2: Range Slider - FULL WIDTH SEPARATE ROW */}
      <DurationRangeSlider
        value={range}
        onRangeChange={onRangeChange}
        totalDurationSeconds={totalDurationSeconds}
        isFirst={isFirst}
        isLast={isLast}
        disabled={disabled}
        testId={`progressbar-item-${index}-duration`}
      />

      {/* Image Upload - Conditional on kind (visibility.showImage) */}
      {visibility.showImage && (
        <Box>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mb: 0.5, display: 'block' }}
          >
            Image
          </Typography>
          <MediaUploadField
            value={imageUrl || null}
            onChange={handleImageChange}
            mediaType="image"
            disabled={disabled}
            size="small"
            showPreview
            placeholder="Upload item image"
          />
        </Box>
      )}

      {/* Title - Always visible */}
      {visibility.showTitle && (
        <Box>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mb: 0.5, display: 'block' }}
          >
            Title
          </Typography>
          {renderTitleField ? renderTitleField({
            index,
            value: title,
            onChange: onTitleChange,
            disabled,
            label: 'Title',
            placeholder: 'Enter item title',
            testId: `progressbar-item-${index}-title`,
          }) : (
            <LocalTextInput
              value={title}
              onChange={onTitleChange}
              disabled={disabled}
              label="Title"
              placeholder="Enter item title"
              testId={`progressbar-item-${index}-title`}
            />
          )}
        </Box>
      )}

      {/* Subtitle - Conditional based on itemType */}
      {visibility.showSubtitle && (
        <Box>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mb: 0.5, display: 'block' }}
          >
            Subtitle
          </Typography>
          {renderSubtitleField ? renderSubtitleField({
            index,
            value: subtitle,
            onChange: onSubtitleChange,
            disabled,
            label: 'Subtitle',
            placeholder: 'Enter subtitle',
            testId: `progressbar-item-${index}-subtitle`,
            multiline: true,
            rows: 2,
          }) : (
            <LocalTextInput
              value={subtitle}
              onChange={onSubtitleChange}
              disabled={disabled}
              label="Subtitle"
              placeholder="Enter subtitle"
              testId={`progressbar-item-${index}-subtitle`}
              multiline
              rows={2}
            />
          )}
        </Box>
      )}

      {/* Description - Conditional based on itemType */}
      {visibility.showDescription && (
        <Box>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mb: 0.5, display: 'block' }}
          >
            Description
          </Typography>
          {renderDescriptionField ? renderDescriptionField({
            index,
            value: description,
            onChange: onDescriptionChange,
            disabled,
            label: 'Description',
            placeholder: 'Enter description',
            testId: `progressbar-item-${index}-description`,
            multiline: true,
            rows: 3,
          }) : (
            <LocalTextInput
              value={description}
              onChange={onDescriptionChange}
              disabled={disabled}
              label="Description"
              placeholder="Enter description"
              testId={`progressbar-item-${index}-description`}
              multiline
              rows={3}
            />
          )}
        </Box>
      )}
    </Box>
  );
};

export default ProgressBarItemEditor;
